const MAX_NUMBER = 100000000;
const MIN_NUMBER = 0;
export const MAX_NUM_OF_CYCLES = 730;

export const numberValidation = (max = MAX_NUMBER) => [
  {
    type: "number",
    min: 1,
    message: "Must be a positive number",
    transform: (value) => (!value ? null : Number(value) ? Number(value) : 0)
  },
  {
    type: "number",
    max,
    message: `Should be less than ${max}`,
    transform: (value) => (!value ? null : Number(value) ? Number(value) : 0)
  }
];

export const numberInputValidator = (errorMessage = "") => ({
  validator: (_, value) => {
    if (!value || /^\d+$/.test(value)) {
      // This regex checks for a non-negative integer
      return Promise.resolve();
    }
    return Promise.reject(new Error(errorMessage));
  }
});

export const minNumberValidation = (minNumber) => () => ({
  validator(_, value) {
    if (value < minNumber) {
      return Promise.reject();
    }
    return Promise.resolve();
  }
});

export const rangeValidationRule = (min = MIN_NUMBER, max = MAX_NUMBER) => [
  {
    type: "number",
    min,
    message: `Must be ${min} or above`,
    transform: (value) => (!value ? null : Number(value) ? Number(value) : 0)
  },
  {
    type: "number",
    max,
    message: `Must be ${max} or below`,
    transform: (value) => (!value ? null : Number(value) ? Number(value) : 0)
  }
];

export const lengthValidation = (len = 49) => ({
  max: len,
  message: `Should be less than ${len} characters`
});

export const digitsOnlyRule = (t) => ({
  pattern: /^\d+$/,
  message: t("new_patient.only_digits_rule")
});

export const numberRangeRule = (t, min, max) => ({
  type: "integer",
  min,
  max,
  transform: (value) => (value === "" || value === undefined || value === null ? undefined : Number(value)),
  message: t("new_patient.number_range_rule", { min, max })
});

export const ssnRule = (t) => ({
  pattern: /^\d{3}-?\d{2}-?\d{4}$/,
  message: t("new_patient.ssn_rule")
});

export function isUrl(_string) {
  const matchpattern =
    /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g;
  return matchpattern.test(_string);
}

export const isEmail = (_string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(_string);
};

export const phoneNumberLengthRegEx = /^\d{10}$/;

export const numOfCyclesOrRefillsValidator = (_, numOfCycles) => {
  if (numOfCycles > MAX_NUM_OF_CYCLES) {
    return Promise.reject();
  } else {
    return Promise.resolve();
  }
};

export const getValidationErrors = async (form, fieldsToValidate) => {
  try {
    if (fieldsToValidate) {
      await form.validateFields(fieldsToValidate);
    } else {
      await form.validateFields();
    }
  } catch (errorsInfo) {
    if (errorsInfo.errorFields?.length) return errorsInfo.errorFields;
  }
  return null;
};

export const validateEmail = (_, value) => {
  if (!value || isEmail(value)) {
    return Promise.resolve();
  }
  return Promise.reject();
};

// Validation patterns for patient application forms
export const validationPatterns = {
  // Alphanumeric pattern - allows only letters (A-Z, a-z) and numbers (0-9)
  alphanumeric: /^[a-zA-Z0-9]*$/
};

export const copayCardRule = (t) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    // Strip hyphens and spaces for validation
    const strippedValue = value.replace(/[-\s]/g, "");

    // Check if alphanumeric only
    if (!/^[a-zA-Z0-9]*$/.test(strippedValue)) {
      return Promise.reject(
        new Error(t("patientApplications.track_tab.error_messages.only_letters_and_numbers_allowed"))
      );
    }

    // Check length (12-16 characters after stripping)
    if (strippedValue.length < 12 || strippedValue.length > 16) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.copay_card_length_error")));
    }

    return Promise.resolve();
  }
});

export const zipCodeRule = (t) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const valueStr = value.toString();

    // First check if it contains non-digit characters
    if (!/^\d+$/.test(valueStr)) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.only_numbers_allowed")));
    }

    // Then check if it's exactly 5 digits
    if (valueStr.length !== 5) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.zip_code_format_error")));
    }

    return Promise.resolve();
  }
});

export const numbersOnlyRule = (t) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const valueStr = value.toString();
    if (!/^\d+$/.test(valueStr)) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.only_numbers_allowed")));
    }

    return Promise.resolve();
  }
});

export const binRule = (t) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    // Check if alphanumeric only
    if (!/^[a-zA-Z0-9]*$/.test(value)) {
      return Promise.reject(
        new Error(t("patientApplications.track_tab.error_messages.only_letters_and_numbers_allowed"))
      );
    }

    // Check length (6-8 characters)
    if (value.length < 6 || value.length > 8) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.bin_length_error")));
    }

    return Promise.resolve();
  }
});

export const cvvRule = (t) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const valueStr = value.toString();

    // First check if it contains non-digit characters
    if (!/^\d+$/.test(valueStr)) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.only_numbers_allowed")));
    }

    // Then check if it's exactly 3 digits
    if (valueStr.length !== 3) {
      return Promise.reject(new Error(t("patientApplications.track_tab.error_messages.cvv_format_error")));
    }

    return Promise.resolve();
  }
});

export const alphanumericLengthRule = (t, maxLength) => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const valueStr = value.toString();

    // First check if alphanumeric only - this will only show after length is valid
    if (!/^[a-zA-Z0-9]*$/.test(valueStr)) {
      return Promise.reject(
        new Error(t("patientApplications.track_tab.error_messages.only_letters_and_numbers_allowed"))
      );
    }

    // Then check length - this will be shown first if exceeded
    if (valueStr.length > maxLength) {
      return Promise.reject(
        new Error(
          t("patientApplications.track_tab.error_messages.should_be_less_than_or_equal_to_number_of_characters", {
            numberOfCharacters: maxLength
          })
        )
      );
    }

    return Promise.resolve();
  }
});

export const maxBenefitRule = (t) => ({
  validator: (_, value) => {
    if (!value || value === "") {
      return Promise.resolve();
    }

    const valueStr = value.toString().trim();

    // Count only digits (exclude decimal point)
    const digitCount = valueStr.replace(/\./g, "").length;

    if (digitCount > 7) {
      return Promise.reject(
        new Error(
          t("patientApplications.track_tab.error_messages.should_be_less_than_or_equal_to_number_of_digits", {
            numberOfDigits: 7
          })
        )
      );
    }

    return Promise.resolve();
  }
});
